// ════════════════════════════════════════════════════════════════════════════
// LpaModal — modal a11y-first com role=dialog, focus trap, ESC, click-outside
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect } from "react";
import { useFieldId } from "./helpers/useFieldId";
import { useEscapeKey } from "./helpers/useEscapeKey";
import { useFocusTrap } from "./helpers/useFocusTrap";

export type LpaModalSize = "sm" | "md" | "lg" | "xl" | "fullscreen";

const SIZE_WIDTH: Record<LpaModalSize, string> = {
  sm: "min(400px, 92vw)",
  md: "min(560px, 92vw)",
  lg: "min(800px, 94vw)",
  xl: "min(1100px, 96vw)",
  fullscreen: "100vw",
};

export interface LpaModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Conteúdo do modal */
  children: React.ReactNode;
  /** Footer (botões de ação) */
  footer?: React.ReactNode;
  size?: LpaModalSize;
  /** ESC fecha (default true) */
  closeOnEscape?: boolean;
  /** Click no overlay fecha (default true) */
  closeOnOverlayClick?: boolean;
  /** Esconder o botão "X" do header */
  hideCloseButton?: boolean;
  /** Footer alinhado à direita (default) ou space-between */
  footerAlign?: "right" | "between" | "left";
  testId?: string;
}

export default function LpaModal({
  open,
  onClose,
  title,
  children,
  footer,
  size = "md",
  closeOnEscape = true,
  closeOnOverlayClick = true,
  hideCloseButton = false,
  footerAlign = "right",
  testId,
}: LpaModalProps): React.ReactElement | null {
  const { fieldId: titleId } = useFieldId();
  const containerRef = useFocusTrap<HTMLDivElement>(open);

  useEscapeKey(onClose, open && closeOnEscape);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      onClick={closeOnOverlayClick ? onClose : undefined}
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--lpa-overlay)",
        backdropFilter: "blur(2px)",
        zIndex: 1000,
        display: "grid",
        placeItems: size === "fullscreen" ? "stretch" : "start center",
        paddingTop: size === "fullscreen" ? 0 : "8vh",
        animation: "lpa-fade-in 0.2s ease-out",
      }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: SIZE_WIDTH[size],
          maxHeight: size === "fullscreen" ? "100vh" : "min(86vh, 720px)",
          background: "var(--lpa-card)",
          border: "1px solid var(--lpa-border)",
          borderRadius: size === "fullscreen" ? 0 : "var(--lpa-radius-xl)",
          boxShadow: "var(--lpa-shadow-xl)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "lpa-scale-in 0.18s ease-out",
        }}
      >
        {/* Header */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid var(--lpa-border)",
            flexShrink: 0,
          }}
        >
          <h2
            id={titleId}
            style={{
              fontSize: "var(--lpa-text-2xl)",
              fontWeight: 600,
              color: "var(--lpa-text)",
              margin: 0,
              fontFamily: "var(--lpa-font-serif)",
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </h2>
          {!hideCloseButton && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar modal"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--lpa-muted)",
                cursor: "pointer",
                padding: 6,
                borderRadius: "var(--lpa-radius-md)",
                display: "grid",
                placeItems: "center",
                fontSize: 18,
                lineHeight: 1,
                transition: "background var(--lpa-tr-fast), color var(--lpa-tr-fast)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--lpa-surface)";
                e.currentTarget.style.color = "var(--lpa-text)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--lpa-muted)";
              }}
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
        </header>

        {/* Body (scrollável) */}
        <div
          style={{
            padding: "20px",
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
          }}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <footer
            style={{
              display: "flex",
              gap: 8,
              padding: "14px 20px",
              borderTop: "1px solid var(--lpa-border)",
              background: "var(--lpa-surface)",
              flexShrink: 0,
              justifyContent:
                footerAlign === "between"
                  ? "space-between"
                  : footerAlign === "left"
                    ? "flex-start"
                    : "flex-end",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
